import { PRODUCTS, type ProductCard } from "./products.js";

/**
 * Returns the ISO week number (1-53) for a given UTC date. Used as part
 * of the per-week shuffle seed so the order rotates predictably.
 */
export function isoWeek(date: Date): number {
  // Copy date so we don't mutate caller's. Per ISO-8601, week starts on
  // Monday and the first week of the year contains Jan 4th.
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const diffMs = d.getTime() - yearStart.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.ceil((diffMs / dayMs + 1) / 7);
}

/** djb2 hash — fast, deterministic, good enough for shuffle seeding. */
function hashString(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  return h >>> 0;
}

/** Mulberry32 — small deterministic PRNG seeded by a 32-bit integer. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministically shuffle the cross-promo product list so every user
 * sees a stable order within a calendar week, but the order rotates
 * across weeks and varies across users. Pure function — no time/random
 * side effects, takes the date as a parameter.
 */
export function orderProductsForUser(opts: {
  userId: number | string | null;
  date: Date;
  products?: ProductCard[];
}): ProductCard[] {
  const list = [...(opts.products ?? PRODUCTS)];
  const seedKey = `${String(opts.userId ?? "anon")}:${String(opts.date.getUTCFullYear())}:W${String(isoWeek(opts.date))}`;
  const rand = mulberry32(hashString(seedKey));

  // Fisher-Yates shuffle.
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = list[i] as ProductCard;
    list[i] = list[j] as ProductCard;
    list[j] = tmp;
  }
  return list;
}
